// #ux-redesign-start: ReportViewer rediseñado (Patients).
// Antes: paginador con 7+ items, números azules cyan, dos copias del paginador
// arriba y abajo del PDF — abrumador en mobile.
//
// Ahora:
//   - Toolbar sticky en top: prev / "X de N" input editable / next + zoom +/- + reset + download.
//   - Mobile-first: en xs los controles se distribuyen en 2 filas para que cada
//     touch target sea ≥ 44px.
//   - i18n completo (label aria + tooltips).
//   - Keyboard: ← / → navegan; +/- zoomean.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslate } from 'react-translate';
import ReportViewerRenderer from './ReportViewerRenderer';
import MultimediaService from 'services/MultimediaService';
import { LoadingBlock } from 'components/ds';

const ChevronLeftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const ZoomInIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
    <line x1="11" y1="8" x2="11" y2="14" />
    <line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);

const ZoomOutIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
    <line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);

const ResetZoomIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="1 4 1 10 7 10" />
    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
  </svg>
);

const base64ToArrayBuffer = (base64) => {
  const binary = window.atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return [...bytes];
};

const ZOOM_LEVELS = [0.5, 0.75, 1, 1.25, 1.5, 2];

const ReportViewer = ({ study, activeTab }) => {
  const t = useTranslate('laboratory-detail');
  const mounted = useRef(false);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const [numPages, setNumPages] = useState();
  const [pageHeight, setPageHeight] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [zoomIdx, setZoomIdx] = useState(2); // 1.0x

  // Load file
  useEffect(() => {
    if (study.reportFile.length <= 25) {
      setLoading(true);
      MultimediaService.getReportFile(study.reportFile).then((res) => {
        if (mounted.current && !file && activeTab === '3' && res) {
          setLoading(false);
          setFile(base64ToArrayBuffer(res.data));
        }
      });
    }
    mounted.current = true;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mounted.current && !file && activeTab === '3' && study.reportFile.length > 25) {
      setFile(base64ToArrayBuffer(study.reportFile));
    }
  }, [mounted.current, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPageInput(String(pageNumber));
  }, [pageNumber]);

  const goPrev = useCallback(() => {
    if (loadingPDF) return;
    setPageNumber((n) => Math.max(1, n - 1));
  }, [loadingPDF]);

  const goNext = useCallback(() => {
    if (loadingPDF) return;
    setPageNumber((n) => Math.min(numPages || n, n + 1));
  }, [loadingPDF, numPages]);

  const zoomIn = () => setZoomIdx((i) => Math.min(ZOOM_LEVELS.length - 1, i + 1));
  const zoomOut = () => setZoomIdx((i) => Math.max(0, i - 1));
  const zoomReset = () => setZoomIdx(2);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
      else if (e.key === '+' || e.key === '=') zoomIn();
      else if (e.key === '-') zoomOut();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goPrev, goNext]);

  const handlePageInputChange = (e) => setPageInput(e.target.value);
  const handlePageInputCommit = () => {
    const n = parseInt(pageInput, 10);
    if (Number.isFinite(n) && n >= 1 && n <= (numPages || 1)) {
      setPageNumber(n);
    } else {
      setPageInput(String(pageNumber));
    }
  };

  if (!study || !Object.keys(study).length) return <></>;
  if (loading) return <LoadingBlock />;

  const zoom = ZOOM_LEVELS[zoomIdx];

  return (
    <div className="ds-pdfviewer">
      <div className="ds-pdfviewer__toolbar" role="toolbar" aria-label="PDF">
        <div className="ds-pdfviewer__group">
          <button
            type="button"
            className="ds-pdfviewer__btn"
            onClick={goPrev}
            disabled={pageNumber <= 1 || loadingPDF}
            aria-label={t('pdf-prev')}
            title={t('pdf-prev')}
          >
            <ChevronLeftIcon />
          </button>
          <div className="ds-pdfviewer__page">
            <input
              type="text"
              inputMode="numeric"
              value={pageInput}
              onChange={handlePageInputChange}
              onBlur={handlePageInputCommit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.target.blur();
                }
              }}
              className="ds-pdfviewer__page-input"
              aria-label={t('pdf-page')}
            />
            <span className="ds-pdfviewer__page-of">
              {t('pdf-of')} <strong>{numPages || '—'}</strong>
            </span>
          </div>
          <button
            type="button"
            className="ds-pdfviewer__btn"
            onClick={goNext}
            disabled={pageNumber >= (numPages || 1) || loadingPDF}
            aria-label={t('pdf-next')}
            title={t('pdf-next')}
          >
            <ChevronRightIcon />
          </button>
        </div>

        <div className="ds-pdfviewer__group ds-pdfviewer__group--zoom">
          <button
            type="button"
            className="ds-pdfviewer__btn"
            onClick={zoomOut}
            disabled={zoomIdx <= 0}
            aria-label={t('pdf-zoom-out')}
            title={t('pdf-zoom-out')}
          >
            <ZoomOutIcon />
          </button>
          <button
            type="button"
            className="ds-pdfviewer__btn ds-pdfviewer__btn--text"
            onClick={zoomReset}
            aria-label={t('pdf-zoom-reset')}
            title={t('pdf-zoom-reset')}
          >
            {Math.round(zoom * 100)}%
          </button>
          <button
            type="button"
            className="ds-pdfviewer__btn"
            onClick={zoomIn}
            disabled={zoomIdx >= ZOOM_LEVELS.length - 1}
            aria-label={t('pdf-zoom-in')}
            title={t('pdf-zoom-in')}
          >
            <ZoomInIcon />
          </button>
          <button
            type="button"
            className="ds-pdfviewer__btn ds-pdfviewer__btn--icon"
            onClick={zoomReset}
            aria-label={t('pdf-zoom-reset')}
            title={t('pdf-zoom-reset')}
          >
            <ResetZoomIcon />
          </button>
        </div>
      </div>

      <div
        className="ds-pdfviewer__page-wrap"
        style={{ '--ds-pdf-zoom': zoom }}
      >
        {file && (
          <ReportViewerRenderer
            file={file}
            setNumPages={setNumPages}
            pageHeight={pageHeight}
            setPageHeight={setPageHeight}
            pageNumber={pageNumber}
            setLoadingPDF={setLoadingPDF}
            zoom={zoom}
          />
        )}
      </div>
    </div>
  );
};

export default React.memo(ReportViewer);
// #ux-redesign-end
